import {
    Box,
    Checkbox,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Popover,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SettingsIcon from "@mui/icons-material/Settings";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import {
    classifyAvailsLicenseWindowStatus,
    formatAvailsPacificDateTime,
    parseAvailsDateValue,
} from "../../../utilities/helpers/formatUnix";
import EnumColumnFilter from "./EnumColumnFilter";
import {
    ALL_COLUMN_DEFS,
    ColumnDef,
    ColumnKey,
    FORMAT_PROFILE_OPTIONS,
    LICENSE_TYPE_OPTIONS,
    loadColumnPreferences,
    saveColumnPreferences,
} from "./availsPlusColumnConfig";
import {
    BRANDING_RIGHTS_CHECKED_VALUE,
    HDR_CHECKED_VALUE,
    isBrandingRightsChecked,
    isHdrChecked,
} from "./availsPlusOptions";
import SlateIndicator, { SlateStatus } from "./SlateIndicator";
import RokuIndicator, { RokuStatus } from "./RokuIndicator";
import { SlateMismatch } from "./slateUtils";
import {
    ColumnFiltersState,
    FILTERABLE_COLUMN_CONFIG,
    FilterableColumnKey,
    isColumnFilterActive,
    isFilterableColumn,
} from "./availsPlusFilterConfig";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

dayjs.extend(utc);
dayjs.extend(timezone);

const AVAILS_PACIFIC_TZ = "America/Los_Angeles";
const AVAILS_PACIFIC_DATETIME_FORMAT = "MM/DD/YYYY, h:mm A";
const ANNOUNCE_DATE_PLACEHOLDER = "MM/DD/YYYY, h:mm AM";

function getPacificTzAbbreviation(value?: Dayjs | null): string {
    const date = value?.isValid() ? value.toDate() : new Date();
    return (
        new Intl.DateTimeFormat("en-US", {
            timeZone: AVAILS_PACIFIC_TZ,
            timeZoneName: "short",
        })
            .formatToParts(date)
            .find((part) => part.type === "timeZoneName")?.value ?? "PT"
    );
}

export interface AvailsPlusRow {
    rowId: string;
    window_index: number;
    version: number;
    slate_status: SlateStatus;
    slate_mismatches?: SlateMismatch[];
    roku_status: RokuStatus;
    name: string;
    reference_id: string;
    content_type: string;
    license_type: string;
    format_profile: string;
    playback_start_date: string | number;
    playback_end_date: string | number;
    coming_soon_date: string | number;
    branding_rights: string;
    hdr: string;
    license_rights_description: string;
    licensor: string;
    season_number?: number | string;
    episode_number?: number | string;
    episode_count?: number | string;
    season_count?: number | string;
    series_ref_id?: string;
    season_ref_id?: string;
    series_title?: string;
    season_title?: string;
    caption_included?: boolean;
}

type SortOrder = "asc" | "desc" | null;

type EditableField =
    | "license_type"
    | "format_profile"
    | "coming_soon_date"
    | "playback_start_date"
    | "playback_end_date"
    | "branding_rights"
    | "hdr"
    | "license_rights_description";

export type { EditableField, ColumnKey, ColumnDef };

const LICENSE_RIGHTS_OPTIONS = ["", "SWP", "SDP"];
const CHECKBOX_FIELDS = new Set<EditableField>(["branding_rights", "hdr"]);
const PLAYBACK_TIME_FIELDS = new Set<EditableField>([
    "playback_start_date",
    "playback_end_date",
]);
const WINDOW_DATETIME_FIELDS = new Set<EditableField>([
    "coming_soon_date",
    "playback_start_date",
    "playback_end_date",
]);

const COLUMN_DEF_BY_KEY = Object.fromEntries(
    ALL_COLUMN_DEFS.map((column) => [column.key, column])
) as Record<ColumnKey, ColumnDef>;

const EPISODE_ONLY_COLUMNS = new Set<ColumnKey>([
    "season_number",
    "episode_number",
    "episode_count",
    "season_count",
    "series_ref_id",
    "season_ref_id",
    "series_title",
    "season_title",
]);

/** Room for MUI small Checkbox hit target (~38px) + horizontal padding. */
const CHECKBOX_COLUMN_WIDTH = 56;

const catalogTableCheckboxCellSx = {
    width: CHECKBOX_COLUMN_WIDTH,
    minWidth: CHECKBOX_COLUMN_WIDTH,
    maxWidth: CHECKBOX_COLUMN_WIDTH,
    boxSizing: "border-box",
    overflow: "visible",
    verticalAlign: "middle",
    px: 1,
    py: 0.75,
    // Avoid MUI sticky-header clipping the checkbox outline.
    "&.MuiTableCell-stickyHeader": {
        overflow: "visible",
        zIndex: 3,
    },
} as const;

const catalogTableCheckboxSx = {
    p: 0.5,
    m: 0,
    // Keep the visible box fully inside the cell.
    "& .MuiSvgIcon-root": {
        fontSize: 20,
    },
} as const;

const catalogTableSx = {
    tableLayout: "fixed",
    width: "100%",
    "& .MuiTableCell-head": {
        overflow: "visible",
    },
    "& .MuiTableCell-stickyHeader": {
        overflow: "visible",
    },
} as const;

function isEditableField(key: ColumnKey): key is EditableField {
    return (
        key === "license_type" ||
        key === "format_profile" ||
        key === "coming_soon_date" ||
        key === "playback_start_date" ||
        key === "playback_end_date" ||
        key === "branding_rights" ||
        key === "hdr" ||
        key === "license_rights_description"
    );
}

const LICENSE_TYPE_OPTIONS_LOCAL = LICENSE_TYPE_OPTIONS;
const FORMAT_PROFILE_OPTIONS_LOCAL = FORMAT_PROFILE_OPTIONS;

function isCheckboxFieldChecked(field: EditableField, value: string): boolean {
    if (field === "branding_rights") return isBrandingRightsChecked(value);
    if (field === "hdr") return isHdrChecked(value);
    return false;
}

function checkboxFieldValue(field: EditableField, checked: boolean): string {
    if (field === "branding_rights") {
        return checked ? BRANDING_RIGHTS_CHECKED_VALUE : "";
    }
    if (field === "hdr") {
        return checked ? HDR_CHECKED_VALUE : "";
    }
    return checked ? "True" : "";
}

function displayCellValue(value: unknown): string {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
}

function getCatalogRowKey(row: AvailsPlusRow): string {
    const start = String(row.playback_start_date ?? "");
    const end = String(row.playback_end_date ?? "");
    if (start || end) {
        return `${row.reference_id}#${row.window_index}#${start}#${end}`;
    }
    return row.rowId || `${row.reference_id}#${row.window_index}`;
}

function toPacificDayjs(value: string | number): Dayjs | null {
    const parsed = parseAvailsDateValue(value);
    if (!parsed) return null;
    return dayjs(parsed).tz(AVAILS_PACIFIC_TZ);
}

function pacificDayjsToUtcIso(value: Dayjs | null): string {
    if (!value?.isValid()) return "";
    return value.tz(AVAILS_PACIFIC_TZ, true).utc().toISOString();
}
function startAnnounceDateEdit(
    row: AvailsPlusRow,
    canEdit: boolean,
    onStartEdit: () => void,
    setDateDraft: (value: Dayjs | null) => void,
    setDateInputError: (value: string | null) => void
) {
    if (!canEdit) return;
    setDateDraft(toPacificDayjs(row.coming_soon_date));
    setDateInputError(null);
    onStartEdit();
}
function TruncatedCell({
    value,
    align = "left",
    maxWidth,
}: {
    value?: string | null;
    align?: "left" | "center" | "right";
    maxWidth?: number | string;
}) {
    const display = displayCellValue(value);
    if (display === "-") {
        return (
            <TableCell align={align} sx={{ maxWidth, overflow: "hidden" }}>
                {display}
            </TableCell>
        );
    }
    return (
        <TableCell align={align} sx={{ maxWidth, overflow: "hidden" }}>
            <Tooltip title={display} arrow placement="top">
                <Box
                    component="span"
                    sx={{
                        display: "block",
                        maxWidth: maxWidth ?? "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {display}
                </Box>
            </Tooltip>
        </TableCell>
    );
}

function getTruncatedMaxWidth(key: ColumnKey): number | undefined {
    switch (key) {
        case "name":
            return 220;
        case "reference_id":
            return 160;
        case "licensor":
            return 140;
        case "license_rights_description":
            return 180;
        case "series_ref_id":
        case "season_ref_id":
        case "series_title":
        case "season_title":
            return 160;
        default:
            return undefined;
    }
}

function canEditWindowDateTimeForRow(canEdit: boolean, row: AvailsPlusRow): boolean {
    if (!canEdit) return false;
    return (
        classifyAvailsLicenseWindowStatus(
            row.playback_start_date,
            row.playback_end_date
        ) !== "past"
    );
}

function canEditAnnounceDateForRow(canEdit: boolean, row: AvailsPlusRow): boolean {
    return canEditWindowDateTimeForRow(canEdit, row);
}

/**
 * Apply a time-of-day onto the locked Pacific calendar date of an existing instant.
 */
function applyPacificTimeOntoLockedDate(
    lockedInstant: string | number,
    timeValue: Dayjs | null
): Dayjs | null {
    const locked = toPacificDayjs(lockedInstant);
    if (!locked?.isValid() || !timeValue?.isValid()) return null;
    return locked
        .hour(timeValue.hour())
        .minute(timeValue.minute())
        .second(timeValue.second())
        .millisecond(0);
}

interface EditableCellProps {
    row: AvailsPlusRow;
    field: EditableField;
    canEdit: boolean;
    isEditing: boolean;
    onStartEdit: () => void;
    onCancelEdit: () => void;
    onSave: (field: EditableField, value: string) => Promise<void>;
}

function EditableCell({
    row,
    field,
    canEdit,
    isEditing,
    onStartEdit,
    onCancelEdit,
    onSave,
}: EditableCellProps) {
    const [draft, setDraft] = useState<string>(() => String(row[field] ?? ""));
    const [dateDraft, setDateDraft] = useState<Dayjs | null>(() =>
        WINDOW_DATETIME_FIELDS.has(field) ? toPacificDayjs(row[field] as string | number) : null
    );
    const [dateInputError, setDateInputError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const announceDateAnchorRef = useRef<HTMLTableCellElement>(null);

    const displayValue = WINDOW_DATETIME_FIELDS.has(field)
        ? formatAvailsPacificDateTime(row[field] as string | number)
        : CHECKBOX_FIELDS.has(field)
          ? isCheckboxFieldChecked(field, String(row[field] ?? ""))
              ? field === "hdr"
                  ? HDR_CHECKED_VALUE
                  : BRANDING_RIGHTS_CHECKED_VALUE
              : "-"
          : displayCellValue(row[field]);

    const commit = async (nextValue: string) => {
        setSaving(true);
        try {
            await onSave(field, nextValue);
            onCancelEdit();
        } finally {
            setSaving(false);
        }
    };

    const openWindowDateTimeEditor = () => {
        if (!canEdit) return;
        setDateDraft(toPacificDayjs(row[field] as string | number));
        setDateInputError(null);
        onStartEdit();
    };

    if (CHECKBOX_FIELDS.has(field)) {
        const checked = isCheckboxFieldChecked(field, String(row[field] ?? ""));
        return (
            <TableCell align="center" sx={catalogTableCheckboxCellSx}>
                <Checkbox
                    size="small"
                    checked={checked}
                    disabled={!canEdit || saving}
                    onChange={(event) => {
                        void commit(checkboxFieldValue(field, event.target.checked));
                    }}
                    inputProps={{
                        "aria-label":
                            field === "hdr" ? "HDR10" : "Branding Rights",
                    }}
                    sx={catalogTableCheckboxSx}
                />
            </TableCell>
        );
    }

    if (PLAYBACK_TIME_FIELDS.has(field)) {
        const lockedInstant = row[field] as string | number;
        const lockedDateLabel =
            toPacificDayjs(lockedInstant)?.format("MM/DD/YYYY") || "—";
        const fieldLabel =
            field === "playback_start_date" ? "playback start" : "playback end";

        const saveTime = () => {
            const next = applyPacificTimeOntoLockedDate(lockedInstant, dateDraft);
            if (!next?.isValid()) {
                setDateInputError("Enter a valid Pacific time.");
                return;
            }
            setDateInputError(null);
            void commit(pacificDayjsToUtcIso(next));
        };

        return (
            <>
                <TableCell
                    ref={announceDateAnchorRef}
                    align="center"
                    onClick={openWindowDateTimeEditor}
                    sx={{
                        cursor: canEdit ? "pointer" : "default",
                        whiteSpace: "nowrap",
                        bgcolor: isEditing ? "action.selected" : undefined,
                    }}
                >
                    {canEdit ? (
                        <Tooltip title={`Click to edit ${fieldLabel} time (date locked)`} arrow>
                            <Box component="span">{displayValue}</Box>
                        </Tooltip>
                    ) : (
                        displayValue
                    )}
                </TableCell>
                <Popover
                    open={isEditing}
                    anchorEl={announceDateAnchorRef.current}
                    onClose={onCancelEdit}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Box sx={{ width: 300, maxWidth: "95vw", p: 2 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 1.5, lineHeight: 1.4 }}
                        >
                            Date is locked to {lockedDateLabel} (Pacific). Only the
                            time can be changed. Airtable date changes will reset the
                            time.
                        </Typography>
                        <TimePicker
                            value={dateDraft}
                            onChange={(value) => {
                                setDateDraft(value);
                                if (value?.isValid()) setDateInputError(null);
                            }}
                            ampm
                            timezone={AVAILS_PACIFIC_TZ}
                            disabled={saving}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    error: Boolean(dateInputError),
                                    helperText: dateInputError ?? undefined,
                                    onKeyDown: (e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            saveTime();
                                        }
                                        if (e.key === "Escape") {
                                            e.preventDefault();
                                            onCancelEdit();
                                        }
                                    },
                                },
                            }}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 0.25,
                                mt: 1,
                            }}
                        >
                            <Tooltip title="Save" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Save ${fieldLabel} time`}
                                        disabled={saving}
                                        onClick={saveTime}
                                    >
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Cancel" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Cancel edit"
                                        disabled={saving}
                                        onClick={onCancelEdit}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Popover>
            </>
        );
    }

    if (field === "coming_soon_date") {
        const pacificTzAbbreviation = getPacificTzAbbreviation(dateDraft);
        const announceDateHelper = `Type a date and time or use the calendar. Selected as ${pacificTzAbbreviation}, saved in UTC.`;

        const saveDate = () => {
            if (!dateDraft?.isValid()) {
                setDateInputError("Enter a valid Pacific date and time.");
                return;
            }
            setDateInputError(null);
            void commit(pacificDayjsToUtcIso(dateDraft));
        };

        const clearDate = () => {
            setDateInputError(null);
            void commit("");
        };

        const openAnnounceDateEditor = () =>
            startAnnounceDateEdit(
                row,
                canEdit,
                onStartEdit,
                setDateDraft,
                setDateInputError
            );

        return (
            <>
                <TableCell
                    ref={announceDateAnchorRef}
                    align="center"
                    onClick={openAnnounceDateEditor}
                    sx={{
                        cursor: canEdit ? "pointer" : "default",
                        whiteSpace: "nowrap",
                        bgcolor: isEditing ? "action.selected" : undefined,
                    }}
                >
                    {canEdit ? (
                        <Tooltip title="Click to edit announce date (Pacific time)" arrow>
                            <Box component="span">{displayValue}</Box>
                        </Tooltip>
                    ) : (
                        displayValue
                    )}
                </TableCell>
                <Popover
                    open={isEditing}
                    anchorEl={announceDateAnchorRef.current}
                    onClose={onCancelEdit}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Box sx={{ width: 380, maxWidth: "95vw", p: 2 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 1.5, lineHeight: 1.4 }}
                        >
                            {announceDateHelper}
                        </Typography>
                        <DateTimePicker
                            value={dateDraft}
                            onChange={(value) => {
                                setDateDraft(value);
                                if (value?.isValid()) {
                                    setDateInputError(null);
                                }
                            }}
                            onAccept={(value) => {
                                if (value?.isValid()) {
                                    setDateInputError(null);
                                    void commit(pacificDayjsToUtcIso(value));
                                }
                            }}
                            closeOnSelect={false}
                            ampm
                            format={AVAILS_PACIFIC_DATETIME_FORMAT}
                            timezone={AVAILS_PACIFIC_TZ}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            disabled={saving}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    placeholder: ANNOUNCE_DATE_PLACEHOLDER,
                                    error: Boolean(dateInputError),
                                    helperText: dateInputError ?? undefined,
                                    onKeyDown: (e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            saveDate();
                                        }
                                        if (e.key === "Escape") {
                                            e.preventDefault();
                                            onCancelEdit();
                                        }
                                    },
                                },
                                openPickerButton: { size: "small" },
                                actionBar: {
                                    actions: ["accept", "cancel", "clear"],
                                },
                                field: { clearable: false },
                            }}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 0.25,
                                mt: 1,
                            }}
                        >
                            <Tooltip title="Save" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Save announce date"
                                        disabled={saving}
                                        onClick={saveDate}
                                    >
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Clear date" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Clear announce date"
                                        disabled={saving}
                                        onClick={clearDate}
                                    >
                                        <HighlightOffIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Cancel" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Cancel edit"
                                        disabled={saving}
                                        onClick={onCancelEdit}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Popover>
            </>
        );
    }

    if (!isEditing) {
        return (
            <TableCell
                align="center"
                onDoubleClick={() => {
                    if (!canEdit) return;
                    setDraft(String(row[field] ?? ""));
                    onStartEdit();
                }}
                sx={{ cursor: canEdit ? "pointer" : "default", whiteSpace: "nowrap" }}
            >
                {displayValue}
            </TableCell>
        );
    }

    const options =
        field === "license_type"
            ? LICENSE_TYPE_OPTIONS_LOCAL
            : field === "format_profile"
              ? FORMAT_PROFILE_OPTIONS_LOCAL
              : LICENSE_RIGHTS_OPTIONS;

    return (
        <TableCell align="center">
            <Select
                size="small"
                value={draft}
                onChange={(e) => {
                    const next = String(e.target.value);
                    setDraft(next);
                    commit(next);
                }}
                disabled={saving}
                sx={{ minWidth: 120 }}
            >
                {options.map((option) => (
                    <MenuItem key={option || "blank"} value={option}>
                        {option || "(blank)"}
                    </MenuItem>
                ))}
            </Select>
        </TableCell>
    );
}

function TitleCell({
    title,
    maxWidth,
    onOpen,
}: {
    title: string;
    maxWidth?: number;
    onOpen: () => void;
}) {
    const display = displayCellValue(title);
    if (display === "-") {
        return (
            <TableCell align="left" sx={{ maxWidth, overflow: "hidden" }}>
                {display}
            </TableCell>
        );
    }

    return (
        <TableCell
            align="left"
            role="button"
            tabIndex={0}
            aria-label={`View details for ${display}`}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen();
                }
            }}
            sx={{
                maxWidth,
                overflow: "hidden",
                cursor: "pointer",
                color: "primary.main",
                "&:hover": { textDecoration: "underline" },
            }}
        >
            <Tooltip title={`${display} — View details`} arrow placement="top">
                <Box
                    component="span"
                    sx={{
                        display: "block",
                        maxWidth: maxWidth ?? "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {display}
                </Box>
            </Tooltip>
        </TableCell>
    );
}

const CatalogTableRow = memo(function CatalogTableRow({
    row,
    rowNumber,
    visibleColumns,
    canEdit,
    editingCell,
    onStartEdit,
    onCancelEdit,
    onSave,
    onRowOpen,
    selectionEnabled = false,
    selected = false,
    onToggleSelect,
    canSyncToSlate = false,
    syncingRefId = null,
    onSyncToSlate,
    syncErrorByRefId,
}: {
    row: AvailsPlusRow;
    rowNumber: number;
    visibleColumns: ColumnDef[];
    canEdit: boolean;
    editingCell: { rowId: string; field: EditableField } | null;
    onStartEdit: (rowId: string, field: EditableField) => void;
    onCancelEdit: () => void;
    onSave: (row: AvailsPlusRow, field: EditableField, value: string) => Promise<void>;
    onRowOpen?: (row: AvailsPlusRow) => void;
    selectionEnabled?: boolean;
    selected?: boolean;
    onToggleSelect?: (rowKey: string) => void;
    canSyncToSlate?: boolean;
    syncingRefId?: string | null;
    onSyncToSlate?: (refId: string) => void | Promise<void>;
    syncErrorByRefId?: Record<string, string>;
}) {
    const isEpisode = row.content_type?.toLowerCase() === "episode";
    const rowKey = row.rowId || getCatalogRowKey(row);

    const renderCell = (column: ColumnDef) => {
        if (column.key === "slate_status") {
            return (
                <TableCell key={column.key} align="center" sx={{ width: 108 }}>
                    <SlateIndicator
                        status={row.slate_status}
                        variant="chip"
                        showLabel
                        mismatches={row.slate_mismatches}
                        referenceId={row.reference_id}
                        title={row.name}
                        canSync={canSyncToSlate}
                        syncing={syncingRefId === row.reference_id}
                        submitError={syncErrorByRefId?.[row.reference_id] || null}
                        onSyncToSlate={
                            onSyncToSlate
                                ? () => onSyncToSlate(row.reference_id)
                                : undefined
                        }
                    />
                </TableCell>
            );
        }

        if (column.key === "roku_status") {
            return (
                <TableCell key={column.key} align="center" sx={{ width: 108 }}>
                    <RokuIndicator status={row.roku_status} showLabel />
                </TableCell>
            );
        }

        if (isEditableField(column.key)) {
            const field = column.key;
            const fieldCanEdit = WINDOW_DATETIME_FIELDS.has(field)
                ? canEditWindowDateTimeForRow(canEdit, row)
                : canEdit;
            return (
                <EditableCell
                    key={field}
                    row={row}
                    field={field}
                    canEdit={fieldCanEdit}
                    isEditing={
                        editingCell?.rowId === row.rowId &&
                        editingCell.field === field
                    }
                    onStartEdit={() => onStartEdit(row.rowId, field)}
                    onCancelEdit={onCancelEdit}
                    onSave={(saveField, value) => onSave(row, saveField, value)}
                />
            );
        }

        if (column.key === "caption_included") {
            const included = row.caption_included !== false;
            return (
                <TableCell key={column.key} align="center">
                    {included ? "True" : "False"}
                </TableCell>
            );
        }

        if (EPISODE_ONLY_COLUMNS.has(column.key) && !isEpisode) {
            return (
                <TruncatedCell key={column.key} value="" align="center" />
            );
        }

        const raw = row[column.key as keyof AvailsPlusRow];
        const maxWidth = getTruncatedMaxWidth(column.key);

        if (column.key === "name" && onRowOpen) {
            return (
                <TitleCell
                    key={column.key}
                    title={String(raw ?? "")}
                    maxWidth={maxWidth ?? column.minWidth}
                    onOpen={() => onRowOpen(row)}
                />
            );
        }

        return (
            <TruncatedCell
                key={column.key}
                value={displayCellValue(raw)}
                align={column.key === "name" ? "left" : "center"}
                maxWidth={maxWidth ?? column.minWidth}
            />
        );
    };

    return (
        <TableRow hover selected={selectionEnabled && selected}>
            {selectionEnabled && (
                <TableCell align="center" sx={catalogTableCheckboxCellSx}>
                    <Checkbox
                        size="small"
                        sx={catalogTableCheckboxSx}
                        checked={selected}
                        disabled={!rowKey}
                        onChange={() => {
                            if (rowKey) onToggleSelect?.(rowKey);
                        }}
                        inputProps={{
                            "aria-label": `Select ${row.name || row.reference_id}`,
                        }}
                    />
                </TableCell>
            )}
            <TableCell align="center" sx={{ width: 48 }}>
                {rowNumber}
            </TableCell>
            {visibleColumns.map(renderCell)}
        </TableRow>
    );
});

interface AvailsPlusCatalogTableProps {
    rows: AvailsPlusRow[];
    rowOffset: number;
    sortColumn: string | null;
    sortOrder: SortOrder;
    onSort: (column: string) => void;
    canEdit: boolean;
    onRowUpdate: (
        row: AvailsPlusRow,
        field: EditableField,
        value: string
    ) => Promise<void>;
    columnFilters: ColumnFiltersState;
    onColumnFilterApply: (
        column: FilterableColumnKey,
        values: string[] | null
    ) => void;
    onRowOpen?: (row: AvailsPlusRow) => void;
    selectionEnabled?: boolean;
    selectedRowKeys?: Set<string>;
    onToggleRowKey?: (rowKey: string) => void;
    selectedRefIds?: Set<string>;
    onToggleRefId?: (
        refId: string,
        options?: { shiftKey?: boolean; ctrlKey?: boolean; index?: number }
    ) => void;
    onTogglePageSelection?: (refIds: string[], selected: boolean) => void;
    canSyncToSlate?: boolean;
    syncingRefId?: string | null;
    onSyncToSlate?: (refId: string) => void | Promise<void>;
    syncErrorByRefId?: Record<string, string>;
}

export default function AvailsPlusCatalogTable({
    rows,
    rowOffset,
    sortColumn,
    sortOrder,
    onSort,
    canEdit,
    onRowUpdate,
    columnFilters,
    onColumnFilterApply,
    onRowOpen,
    selectionEnabled = false,
    selectedRowKeys,
    onToggleRowKey,
    onTogglePageSelection,
    canSyncToSlate = false,
    syncingRefId = null,
    onSyncToSlate,
    syncErrorByRefId,
}: AvailsPlusCatalogTableProps) {
    const [columnPrefs, setColumnPrefs] = useState(loadColumnPreferences);
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
    const [editingCell, setEditingCell] = useState<{
        rowId: string;
        field: EditableField;
    } | null>(null);

    const visibleColumns = useMemo(() => {
        return columnPrefs.order
            .filter((key) => columnPrefs.visible[key] !== false)
            .map((key) => COLUMN_DEF_BY_KEY[key])
            .filter(Boolean);
    }, [columnPrefs]);

    const orderedMenuColumns = useMemo(
        () => columnPrefs.order.map((key) => COLUMN_DEF_BY_KEY[key]).filter(Boolean),
        [columnPrefs.order]
    );

    const updateColumnPrefs = (next: typeof columnPrefs) => {
        setColumnPrefs(next);
        saveColumnPreferences(next);
    };

    const handleColumnDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const order = [...columnPrefs.order];
        const [moved] = order.splice(result.source.index, 1);
        order.splice(result.destination.index, 0, moved);
        updateColumnPrefs({ ...columnPrefs, order });
    };

    const renderSortLabel = useCallback(
        (column: ColumnDef) => {
            if (!column.sortable) {
                return column.label;
            }
            return (
                <TableSortLabel
                    active={sortColumn === column.key}
                    direction={
                        sortColumn === column.key && sortOrder ? sortOrder : "asc"
                    }
                    onClick={() => onSort(column.key)}
                    hideSortIcon={sortColumn !== column.key}
                >
                    {column.label}
                </TableSortLabel>
            );
        },
        [onSort, sortColumn, sortOrder]
    );

    const renderHeaderCell = useCallback(
        (column: ColumnDef) => {
            const align = column.key === "name" ? "left" : "center";
            const filterKey = isFilterableColumn(column.key)
                ? column.key
                : null;
            const filterConfig = filterKey
                ? FILTERABLE_COLUMN_CONFIG[filterKey]
                : null;
            const columnFilterActive =
                filterKey != null &&
                isColumnFilterActive(filterKey, columnFilters);

            return (
                <TableCell
                    key={column.key}
                    align={align}
                    sx={{
                        width: column.minWidth,
                        maxWidth: column.minWidth,
                        fontWeight: 600,
                        ...(columnFilterActive && {
                            bgcolor: "action.selected",
                            borderBottom: 2,
                            borderColor: "primary.main",
                        }),
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent:
                                column.key === "name" ? "flex-start" : "center",
                            gap: 0.25,
                            width: "100%",
                        }}
                    >
                        {renderSortLabel(column)}
                        {filterKey && filterConfig && (
                            <EnumColumnFilter
                                label={column.label}
                                options={filterConfig.options}
                                selectedValues={columnFilters[filterKey]}
                                onApply={(values) =>
                                    onColumnFilterApply(filterKey, values)
                                }
                            />
                        )}
                    </Box>
                </TableCell>
            );
        },
        [columnFilters, onColumnFilterApply, renderSortLabel]
    );

    const pageRowKeys = useMemo(
        () =>
            rows
                .map((row) => row.rowId || getCatalogRowKey(row))
                .filter(Boolean),
        [rows]
    );
    const selectedOnPageCount = pageRowKeys.filter((rowKey) =>
        selectedRowKeys?.has(rowKey)
    ).length;
    const allOnPageSelected =
        selectionEnabled &&
        pageRowKeys.length > 0 &&
        selectedOnPageCount === pageRowKeys.length;
    const someOnPageSelected =
        selectionEnabled && selectedOnPageCount > 0 && !allOnPageSelected;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Tooltip title="Choose and reorder columns">
                    <IconButton
                        size="small"
                        aria-label="Column settings"
                        onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                    >
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={columnMenuAnchor}
                    open={Boolean(columnMenuAnchor)}
                    onClose={() => setColumnMenuAnchor(null)}
                    PaperProps={{ sx: { width: 320, maxHeight: 480 } }}
                >
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Drag to reorder. Check to show columns.
                        </Typography>
                    </Box>
                    <DragDropContext onDragEnd={handleColumnDragEnd}>
                        <Droppable droppableId="avails-plus-columns">
                            {(droppableProvided) => (
                                <div
                                    ref={droppableProvided.innerRef}
                                    {...droppableProvided.droppableProps}
                                >
                                    {orderedMenuColumns.map((column, index) => (
                                        <Draggable
                                            key={column.key}
                                            draggableId={column.key}
                                            index={index}
                                        >
                                            {(draggableProvided, snapshot) => (
                                                <MenuItem
                                                    ref={draggableProvided.innerRef}
                                                    {...draggableProvided.draggableProps}
                                                    dense
                                                    onClick={() =>
                                                        updateColumnPrefs({
                                                            ...columnPrefs,
                                                            visible: {
                                                                ...columnPrefs.visible,
                                                                [column.key]:
                                                                    columnPrefs.visible[column.key] ===
                                                                    false,
                                                            },
                                                        })
                                                    }
                                                    sx={{
                                                        bgcolor: snapshot.isDragging
                                                            ? "action.hover"
                                                            : undefined,
                                                    }}
                                                >
                                                    <Box
                                                        {...draggableProvided.dragHandleProps}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            mr: 0.5,
                                                            color: "text.secondary",
                                                            cursor: "grab",
                                                        }}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        <DragIndicatorIcon fontSize="small" />
                                                    </Box>
                                                    <Checkbox
                                                        size="small"
                                                        checked={
                                                            columnPrefs.visible[column.key] !== false
                                                        }
                                                        sx={{ p: 0.5, mr: 1 }}
                                                        tabIndex={-1}
                                                    />
                                                    {column.label}
                                                </MenuItem>
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Menu>
            </Box>

            <TableContainer
                component={Paper}
                sx={{ width: "100%", overflowX: "auto", overflowY: "visible" }}
            >
                <Table
                    stickyHeader
                    size="small"
                    aria-label="Avails catalog table"
                    sx={catalogTableSx}
                >
                    <TableHead>
                        <TableRow>
                            {selectionEnabled && (
                                <TableCell align="center" sx={catalogTableCheckboxCellSx}>
                                    <Checkbox
                                        size="small"
                                        sx={catalogTableCheckboxSx}
                                        checked={allOnPageSelected}
                                        indeterminate={someOnPageSelected}
                                        disabled={!pageRowKeys.length}
                                        onChange={(event) =>
                                            onTogglePageSelection?.(
                                                pageRowKeys,
                                                event.target.checked
                                            )
                                        }
                        inputProps={{ "aria-label": "Select all titles on this page. Use Shift+click on row checkboxes to select a range." }}
                    />
                </TableCell>
            )}
            <TableCell align="center" sx={{ width: 48, fontWeight: 600, py: 0.75 }}>
                #
            </TableCell>
                            {visibleColumns.map((column) => renderHeaderCell(column))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <CatalogTableRow
                                key={getCatalogRowKey(row)}
                                row={row}
                                rowNumber={rowOffset + index + 1}
                                visibleColumns={visibleColumns}
                                canEdit={canEdit}
                                editingCell={editingCell}
                                onStartEdit={(rowId, field) =>
                                    setEditingCell({ rowId, field })
                                }
                                onCancelEdit={() => setEditingCell(null)}
                                onSave={onRowUpdate}
                                onRowOpen={onRowOpen}
                                selectionEnabled={selectionEnabled}
                                selected={Boolean(
                                    (row.rowId || getCatalogRowKey(row)) &&
                                        selectedRowKeys?.has(
                                            row.rowId || getCatalogRowKey(row)
                                        )
                                )}
                                onToggleSelect={onToggleRowKey}
                                canSyncToSlate={canSyncToSlate}
                                syncingRefId={syncingRefId}
                                onSyncToSlate={onSyncToSlate}
                                syncErrorByRefId={syncErrorByRefId}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
        </LocalizationProvider>
    );
}
